import React, { useRef, useState } from "react";
import {
  Box,
  MenuItem,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Autocomplete,
} from "@mui/material";
import { FormikProps } from "formik";
import dayjs from "dayjs";
import Button from "../ui/elements/buttons/Button";
import { Exhibitor } from "../modules/exhibitor/exhibitorType/Exhibitor";
import { BookingsFilterValues } from "./schema/BookingFiltersType";
import { statusMapper } from "./schema/statusMapper";
import { eventTypeMapper } from "./schema/eventTypeMapper";
import { broadcastTypes } from "./schema/broadcastTypes";
import { paymentStatus } from "./schema/paymentStatus";
import { useUserContext } from "../modules/user/UserContext";
import { useInfiniteExhibitors } from "../modules/exhibitor/queries/useInfiniteExhibitors";
import CircularLoader from "../ui/elements/loader/CircularLoader";

interface BookingFilterPopupProps {
  onClose: () => void;
  setFilters?: any;
  exhibitors?: Exhibitor[];
  formik: FormikProps<BookingsFilterValues>;
  pageType?: string; // Add pageType prop
}

const BookingFilterPopup: React.FC<BookingFilterPopupProps> = ({
  onClose,
  setFilters,
  exhibitors,
  formik,
  pageType, // Get pageType from props
}) => {
  const exhibitorNames: string[] = exhibitors?.map((item) => item.name) || [];
  const showAllFields = pageType !== "players"; // Only show all fields if not "players" page
  const { isAdmin } = useUserContext();

  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const handleInputChange = (event: React.ChangeEvent<{}>, value: string) => {
    setInputValue(value);
    // Simulate loading state when typing
    if (value) {
      setLoading(true);
      // In a real app, you would fetch data here and setLoading(false) when done
      setTimeout(() => setLoading(false), 500);
    }
  };

  const useBlurOnMinuteChange = () => {
    const prevValuesRef = useRef<{ [key: string]: string }>({});
  
    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      key: string,
      setValue: (val: string) => void
    ) => {
      const newVal = e.target.value;
      const prevVal = prevValuesRef.current[key];
  
      prevValuesRef.current[key] = newVal;
      setValue(newVal);
  
      if (!prevVal || prevVal.length < 16 || newVal.length < 16) return;
  
      const prevMinutes = prevVal.slice(14, 16);
      const newMinutes = newVal.slice(14, 16);
  
      if (prevMinutes !== newMinutes) {
        e.target.blur();
      }
    };
  
    return { handleChange };
  };
  const { handleChange } = useBlurOnMinuteChange();  


  return (
    <Box
      sx={{
        height: "100%",
        width: { sm: "500px", xs: "100%" },
        padding: "16px",
      }}
    >
      <Typography marginBottom="24px" fontSize="20px" fontWeight="500">
        Filters
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Always show Exhibitor Field */}
          {isAdmin && (
            <Grid item xs={12} sm={showAllFields ? 6 : 12}>
              <Autocomplete
                multiple
                options={[
                  ...formik.values.exhibitor, // Selected exhibitors first
                  ...exhibitorNames.filter((name) => !formik.values.exhibitor.includes(name)), // Remaining exhibitors
                ]}
                value={formik.values.exhibitor}
                onChange={(event, value) => {
                  formik.setFieldValue("exhibitor", value);
                }}
                onInputChange={handleInputChange}
                inputValue={inputValue}
                loading={loading}
                loadingText={<CircularLoader />}
                noOptionsText="No exhibitors" 
                componentsProps={{
                  paper: {
                    sx: {
                      "& .MuiAutocomplete-noOptions": {
                        color: "red", 
                        padding: "8px 16px", 
                        fontSize: "14px" 
                      }
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Exhibitors"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiInputLabel-root": { color: "black" } }}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      sx={{ color: "black" }}
                      checked={selected}
                    />
                    {option}
                  </li>
                )}
              />
            </Grid>
          )}

          {/* Conditionally show other fields */}
          {showAllFields && (
            <>
              {/* Status Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  id="booking_status"
                  name="booking_status"
                  label="Status"
                  size="small"
                  sx={{ "& .MuiInputLabel-root": { color: "black" } }}
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.booking_status || ""}
                  onChange={formik.handleChange}
                  error={formik.touched.booking_status && Boolean(formik.errors.booking_status)}
                  helperText={formik.touched.booking_status && formik.errors.booking_status}
                >
                  {Object.keys(statusMapper).map((key) => (
                    <MenuItem sx={{ height: '28px' }} key={key} value={key}>
                      {statusMapper[key]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Start DateTime Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start At"
                  type="datetime-local"
                  value={
                    formik.values.startTime
                      ? dayjs(formik.values.startTime).format("YYYY-MM-DDTHH:mm")
                      : ""
                  }
                   onChange={(e) =>
      handleChange(e, "startTime", (val) => {
        const isoString = val ? new Date(val).toISOString() : null;
        formik.setFieldValue("startTime", isoString);
      })
    }
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiInputLabel-root": { color: "black" } }}
                  fullWidth
                  size="small"
                  error={formik.touched.startTime && Boolean(formik.errors.startTime)}
                  helperText={formik.touched.startTime && formik.errors.startTime}
                />
              </Grid>

              {/* End DateTime Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="End At"
                  type="datetime-local"
                  value={
                    formik.values.endTime
                      ? dayjs(formik.values.endTime).format("YYYY-MM-DDTHH:mm")
                      : ""
                  }
                  onChange={(e) =>
      handleChange(e, "endTime", (val) => {
        const isoString = val ? new Date(val).toISOString() : null;
        formik.setFieldValue("endTime", isoString);
      })
    }
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiInputLabel-root": { color: "black" } }}
                  fullWidth
                  size="small"
                  error={formik.touched.endTime && Boolean(formik.errors.endTime)}
                  helperText={formik.touched.endTime && formik.errors.endTime}
                />
              </Grid>

              {/* Event Type Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  id="event_type"
                  name="event_type"
                  label="Event Type"
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ "& .MuiInputLabel-root": { color: "black" } }}
                  value={formik.values.event_type}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.event_type && Boolean(formik.errors.event_type)
                  }
                  helperText={formik.touched.event_type && formik.errors.event_type}
                >
                  {Object.keys(eventTypeMapper).map((key) => (
                    <MenuItem sx={{ height: '28px' }} key={key} value={key}>
                      {eventTypeMapper[key]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Broadcast Type Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  id="broadcast_type"
                  name="broadcast_type"
                  label="Broadcast Type"
                  size="small"
                  sx={{ "& .MuiInputLabel-root": { color: "black" } }}
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.broadcast_type}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.broadcast_type &&
                    Boolean(formik.errors.broadcast_type)
                  }
                  helperText={
                    formik.touched.broadcast_type && formik.errors.broadcast_type
                  }
                >
                  {Object.keys(broadcastTypes).map((key) => (
                    <MenuItem sx={{ height: '28px' }} key={key} value={key}>
                      {broadcastTypes[key]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Payment Status Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  id="payment_status"
                  name="payment_status"
                  label="Payment Status"
                  size="small"
                  sx={{ "& .MuiInputLabel-root": { color: "black" } }}
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.payment_status}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.payment_status &&
                    Boolean(formik.errors.payment_status)
                  }
                  helperText={
                    formik.touched.payment_status && formik.errors.payment_status
                  }
                >
                  {Object.keys(paymentStatus).map((key) => (
                    <MenuItem sx={{ height: '28px' }} key={key} value={key}>
                      {paymentStatus[key]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </>
          )}
        </Grid>

        {/* Buttons: Apply & Reset */}
        <Box display="flex" gap="8px" justifyContent="flex-end" mt="16px">
          <Button
            onClick={() => formik.resetForm()}
            background="white"
            color="black"
          >
            Reset
          </Button>
          <Button type="submit" color="white">
            Apply Filters
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default BookingFilterPopup;